/**
 * 配信一覧ページ（「直近のやりとり」カードを含む。設計書 9.3 / html 図6）。
 *
 * 設計書: `docs/design-docs/deliveries/open-click-analytics.md`。
 * 本文中の `設計書 N.M` はこの md、`html N.M` / `html 図N` は同ディレクトリの
 * `open-click-analytics.html` を指す。
 */
import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Link } from "components/ui/Link";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import {
  type DeliveryItem,
  useCancelDeliveryApi,
  useDeleteDeliveryApi,
  useDeliveriesApi,
} from "hooks/use-delivery-api";
import { Eye, LayoutList, Megaphone, RefreshCw } from "lucide-react";
import { type FC, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { DeliveriesGroupedView } from "routes/DeliveryPage/components/DeliveriesGroupedView";
import { RecentEngagementCard } from "routes/DeliveryPage/components/RecentEngagementCard";
import { DELIVERY_STATUS_BADGE } from "routes/DeliveryPage/delivery-status-badge";
import { formatDateTime } from "utils/relative-time";

type TabStatus = "draft" | "scheduled" | "history";
type ViewMode = "flat" | "grouped";

const TAB_DEFINITIONS: ReadonlyArray<{ key: TabStatus; label: string }> = [
  { key: "history", label: "配信履歴" },
  { key: "scheduled", label: "予約中" },
  { key: "draft", label: "下書き" },
] as const;

const VALID_TABS: ReadonlySet<TabStatus> = new Set(
  TAB_DEFINITIONS.map(t => t.key),
);
const DEFAULT_TAB: TabStatus = "history";

/**
 * URL の ?tab=... を TabStatus に解決する. 未指定 / 不正値はデフォルトに丸める.
 * @param {string|null} raw URL から読んだ値
 * @return {TabStatus} 解決済みタブ
 */
function resolveTab(raw: string | null): TabStatus {
  if (raw && VALID_TABS.has(raw as TabStatus)) {
    return raw as TabStatus;
  }
  return DEFAULT_TAB;
}

type ViewModeToggleProps = {
  value: ViewMode;
  onChange: (next: ViewMode) => void;
};

/**
 * 配信一覧の表示モード切替 (フラット / シリーズ別).
 * @param {ViewModeToggleProps} props 値とハンドラ
 * @return {JSX.Element} トグル
 */
const ViewModeToggle: FC<ViewModeToggleProps> = ({ value, onChange }) => {
  return (
    <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs mb-1">
      <button
        type="button"
        onClick={() => onChange("flat")}
        className={`px-2 py-1 inline-flex items-center gap-1 transition-colors ${
          value === "flat"
            ? "bg-violet-50 text-violet-800 font-semibold"
            : "text-base-content/70 hover:bg-slate-50"
        }`}
        title="フラット表示 (時系列)"
      >
        <LayoutList size={12} aria-hidden />
        フラット
      </button>
      <button
        type="button"
        onClick={() => onChange("grouped")}
        className={`px-2 py-1 inline-flex items-center gap-1 transition-colors ${
          value === "grouped"
            ? "bg-violet-50 text-violet-800 font-semibold"
            : "text-base-content/70 hover:bg-slate-50"
        }`}
        title="シリーズ別折り畳み表示"
      >
        <Megaphone size={12} aria-hidden />
        シリーズ別
      </button>
    </div>
  );
};

export const DeliveriesPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = resolveTab(searchParams.get("tab"));
  const viewMode: ViewMode =
    searchParams.get("view") === "grouped" ? "grouped" : "flat";
  const [page, setPage] = useState(1);

  const { data, loading, error, refetch } = useDeliveriesApi({
    status: tab,
    page,
    limit: 20,
  });
  const { deleteDelivery } = useDeleteDeliveryApi();
  const { cancelDelivery } = useCancelDeliveryApi();

  const [confirmAction, setConfirmAction] = useState<{
    type: "delete" | "cancel";
    id: string;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleTabChange = (newTab: TabStatus) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tab", newTab);
      return next;
    });
    setPage(1);
  };

  const handleViewModeChange = (next: ViewMode): void => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (next === "grouped") {
        params.set("view", "grouped");
      } else {
        params.delete("view");
      }
      return params;
    });
  };

  const handleAction = async () => {
    if (!confirmAction) return;
    setActionLoading(true);
    try {
      if (confirmAction.type === "delete") {
        await deleteDelivery(confirmAction.id);
      } else {
        await cancelDelivery(confirmAction.id);
      }
      setConfirmAction(null);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const totalPages = data ? Math.ceil(data.total / 20) : 1;

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>一斉配信</PageTitle>
        <div className="alert alert-error">
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>一斉配信</PageTitle>
        <Button
          type="button"
          size="sm"
          onClick={() => navigate("/deliveries/new")}
        >
          + 新規配信
        </Button>
      </div>

      {/* 直近のやりとり (設計書 9.3) */}
      <RecentEngagementCard />

      {/* タブ */}
      <div className="flex items-end justify-between gap-3 border-b border-slate-200 mb-6">
        <div className="flex gap-6">
          {TAB_DEFINITIONS.map(t => {
            const isActive = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => handleTabChange(t.key)}
                className={`relative px-2 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-primary"
                    : "text-base-content/60 hover:text-base-content"
                }`}
              >
                {t.label}
                {isActive && (
                  <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary" />
                )}
              </button>
            );
          })}
        </div>
        {tab === "history" && (
          <ViewModeToggle value={viewMode} onChange={handleViewModeChange} />
        )}
      </div>

      {/* グループ表示 (history タブのみ対応) */}
      {tab === "history" && viewMode === "grouped" ? (
        loading ? (
          <div className="flex justify-center py-8">
            <Loading size="md" />
          </div>
        ) : (
          <DeliveriesGroupedView items={data?.items ?? []} />
        )
      ) : (
        /* テーブル */
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          <Table.Root density="compact" zebra stickyHeader>
            <Table.Head sticky>
              <tr>
                <Table.HeaderCell>件名</Table.HeaderCell>
                <Table.HeaderCell>配信先グループ</Table.HeaderCell>
                <Table.HeaderCell>送信者</Table.HeaderCell>
                {tab === "history" && (
                  <Table.HeaderCell>宛先数</Table.HeaderCell>
                )}
                {tab === "scheduled" && (
                  <Table.HeaderCell>予約日時</Table.HeaderCell>
                )}
                {tab === "history" && (
                  <Table.HeaderCell>配信日時</Table.HeaderCell>
                )}
                {tab === "history" && (
                  <Table.HeaderCell>ステータス</Table.HeaderCell>
                )}
                {tab === "draft" && (
                  <Table.HeaderCell>作成日時</Table.HeaderCell>
                )}
                {tab === "scheduled" && (
                  <Table.HeaderCell>作成日時</Table.HeaderCell>
                )}
                <Table.HeaderCell>操作</Table.HeaderCell>
              </tr>
            </Table.Head>
            <Table.Body>
              {loading ? (
                <tr>
                  <td
                    colSpan={tab === "history" ? 7 : 5}
                    className="text-center py-8"
                  >
                    <Loading size="md" />
                  </td>
                </tr>
              ) : data?.items.length === 0 ? (
                <tr>
                  <td
                    colSpan={tab === "history" ? 7 : 5}
                    className="text-center py-8 text-base-content/60"
                  >
                    {tab === "draft" && "下書きがありません"}
                    {tab === "scheduled" && "予約中の配信がありません"}
                    {tab === "history" && "配信履歴がありません"}
                  </td>
                </tr>
              ) : (
                data?.items.map((item: DeliveryItem) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      <Link
                        to={
                          tab === "draft"
                            ? `/deliveries/${item.id}/edit`
                            : `/deliveries/${item.id}`
                        }
                      >
                        {item.subject}
                      </Link>
                    </Table.Cell>
                    <Table.Cell>{item.groupName || "-"}</Table.Cell>
                    <Table.Cell>{item.fromName || item.fromEmail}</Table.Cell>
                    {tab === "history" && (
                      <Table.Cell>
                        <span className="text-success">
                          {item.successCount}
                        </span>
                        /<span className="text-error">{item.failureCount}</span>
                        <span className="text-base-content/60">
                          {" "}
                          ({item.totalRecipients})
                        </span>
                      </Table.Cell>
                    )}
                    {tab === "scheduled" && (
                      <Table.Cell>
                        {formatDateTime(item.scheduledAt, "-")}
                      </Table.Cell>
                    )}
                    {tab === "history" && (
                      <Table.Cell>
                        {formatDateTime(item.sentAt, "-")}
                      </Table.Cell>
                    )}
                    {tab === "history" && (
                      <Table.Cell>
                        <StatusBadge
                          tone={
                            DELIVERY_STATUS_BADGE[item.status]?.tone ??
                            "soft-neutral"
                          }
                        >
                          {DELIVERY_STATUS_BADGE[item.status]?.label ||
                            item.status}
                        </StatusBadge>
                      </Table.Cell>
                    )}
                    {(tab === "draft" || tab === "scheduled") && (
                      <Table.Cell>
                        {formatDateTime(item.createdAt, "-")}
                      </Table.Cell>
                    )}
                    <Table.Cell>
                      <div className="flex gap-2">
                        {tab === "draft" && (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="xs"
                              onClick={() =>
                                navigate(`/deliveries/${item.id}/edit`)
                              }
                            >
                              編集
                            </Button>
                            <Button
                              type="button"
                              onClick={() =>
                                setConfirmAction({
                                  type: "delete",
                                  id: item.id,
                                })
                              }
                              variant="destructive"
                              size="xs"
                            >
                              削除
                            </Button>
                          </>
                        )}
                        {tab === "scheduled" && (
                          <Button
                            type="button"
                            onClick={() =>
                              setConfirmAction({
                                type: "cancel",
                                id: item.id,
                              })
                            }
                            variant="warning"
                            size="xs"
                          >
                            キャンセル
                          </Button>
                        )}
                        {tab === "history" && (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              size="xs"
                              onClick={() => navigate(`/deliveries/${item.id}`)}
                            >
                              <Eye size={14} />
                              詳細
                            </Button>
                            <Button
                              type="button"
                              size="xs"
                              onClick={() =>
                                navigate(`/deliveries/new?copy-from=${item.id}`)
                              }
                            >
                              <RefreshCw size={14} />
                              再配信
                            </Button>
                          </>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </div>
      )}

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <div className="join">
            <Button
              type="button"
              size="sm"
              isJoinItem
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              &laquo;
            </Button>
            <Button type="button" size="sm" isJoinItem>
              {page} / {totalPages}
            </Button>
            <Button
              type="button"
              size="sm"
              isJoinItem
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              &raquo;
            </Button>
          </div>
        </div>
      )}

      {/* 確認モーダル */}
      {confirmAction && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">
              {confirmAction.type === "delete"
                ? "配信を削除しますか？"
                : "予約をキャンセルしますか？"}
            </h3>
            <p className="py-4">この操作は取り消せません。</p>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmAction(null)}
                disabled={actionLoading}
              >
                戻る
              </Button>
              <Button
                type="button"
                variant={
                  confirmAction.type === "delete" ? "destructive" : "warning"
                }
                onClick={handleAction}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <Loading size="sm" />
                ) : confirmAction.type === "delete" ? (
                  "削除"
                ) : (
                  "キャンセル"
                )}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !actionLoading && setConfirmAction(null)}
          />
        </div>
      )}
    </div>
  );
};
